/**
 * UsersTab — redesign v2
 * - Stat tiles: Users / Machines / Online / Distributors
 * - User rows: avatar initials · name + email · role badge · machine count · status pill · last active · actions
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers, toggleUser, getOverview } from '../../API/adminAPI';

const Icon = ({ d, size = 15, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const EditIcon   = 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z';
const OffIcon    = 'M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M8.5 7a4 4 0 115.196 3.796M20 8.5L8.5 20';
const OnIcon     = 'M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M8.5 7a4 4 0 117 0 M20 8l-4 4-2-2';
const PlusIcon   = 'M12 5v14 M5 12h14';
const SearchIcon = 'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z';
const RefreshIcon= 'M1 4v6h6 M23 20v-6h-6 M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15';

const ROLE_STYLES = {
  atoms_admin:       { bg: 'var(--ai-soft)',     text: 'var(--role-atoms)', label: 'ATOMS Admin' },
  oem_admin:         { bg: 'var(--accent-soft)',  text: 'var(--role-oem)',   label: 'OEM Admin' },
  distributor_admin: { bg: 'var(--warn-bg)',      text: 'var(--role-dist)',  label: 'Distributor Admin' },
  user:              { bg: 'var(--neutral-bg)',   text: 'var(--role-user)',  label: 'Regular User' },
};

function RoleBadge({ role }) {
  const s = ROLE_STYLES[role] || ROLE_STYLES.user;
  return (
    <span style={{ display: 'inline-block', background: s.bg, color: s.text, fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 500, borderRadius: 4, padding: '2px 8px' }}>
      {s.label}
    </span>
  );
}

function Avatar({ name }) {
  const initial = (name || 'U')[0].toUpperCase();
  return (
    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-soft)', color: 'var(--accent)', fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {initial}
    </div>
  );
}

function StatTile({ label, value, color = 'var(--ink-900)' }) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', padding: '16px 20px', boxShadow: 'var(--sh-sm)' }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 500, color, letterSpacing: '-0.5px' }}>{value ?? '—'}</div>
    </div>
  );
}

const UsersTab = () => {
  const navigate = useNavigate();
  const [users,   setUsers]   = useState([]);
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [search,  setSearch]  = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [ud, od] = await Promise.all([getUsers(), getOverview().catch(() => null)]);
      setUsers(ud.users || []);
      if (od) setStats(od);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load users');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (userId, e) => {
    e.stopPropagation();
    try { await toggleUser(userId); load(); }
    catch (e) { alert(e?.response?.data?.error || 'Toggle failed'); }
  };

  const filtered = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Stat tiles */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
          <StatTile label="Users"        value={stats.total_users}    />
          <StatTile label="Machines"     value={stats.total_machines} />
          <StatTile label="Online Now"   value={stats.active_machines} color="var(--online)" />
          <StatTile label="Distributors" value={stats.total_dists}    />
        </div>
      )}

      {error && <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-300)', pointerEvents: 'none' }}>
            <Icon d={SearchIcon} />
          </div>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search users…"
            style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-700)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', background: 'none', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', cursor: 'pointer', color: 'var(--ink-500)' }}>
            <Icon d={RefreshIcon} size={14} />
          </button>
          <button onClick={() => navigate('/app/users/new')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--accent)', border: 'none', borderRadius: 'var(--r-md)', cursor: 'pointer', color: '#fff', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500 }}>
            <Icon d={PlusIcon} size={14} color="#fff" />
            New User
          </button>
        </div>
      </div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)', marginBottom: 10 }}>{filtered.length} user{filtered.length !== 1 ? 's' : ''}</div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 140px 80px 100px 110px 80px', gap: 0, borderBottom: '2px solid var(--border-strong)', padding: '10px 16px' }}>
            {['', 'User', 'Role', 'Machines', 'Status', 'Last Active', 'Actions'].map((h, i) => (
              <div key={i} style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)' }}>{h}</div>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>No users found</div>
          ) : filtered.map(u => (
            <div
              key={u.id}
              onClick={() => navigate(`/app/users/${u.id}/edit`, { state: { user: u } })}
              style={{ display: 'grid', gridTemplateColumns: '44px 1fr 140px 80px 100px 110px 80px', gap: 0, padding: '14px 16px', borderBottom: '1px solid var(--border-hair)', cursor: 'pointer', alignItems: 'center', transition: 'background var(--t-fast)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Avatar name={u.username} />
              <div>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)' }}>{u.username}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)', marginTop: 1 }}>{u.email || '—'}</div>
              </div>
              <RoleBadge role={u.role} />
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-700)' }}>{u.machine_count ?? '—'}</div>
              <div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 500, padding: '3px 8px', borderRadius: 4, background: u.is_active ? 'var(--online-bg)' : 'var(--offline-bg)', color: u.is_active ? 'var(--online)' : 'var(--offline)', border: `1px solid ${u.is_active ? 'var(--online-border)' : 'var(--offline-border)'}` }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: u.is_active ? 'var(--online)' : 'var(--offline)', animation: u.is_active ? 'atoms-pulse 2s infinite' : 'none' }} />
                  {u.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)' }}>{u.last_login || '—'}</div>
              <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button title="Edit" onClick={() => navigate(`/app/users/${u.id}/edit`, { state: { user: u } })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 4, borderRadius: 'var(--r-sm)' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-500)'}>
                  <Icon d={EditIcon} />
                </button>
                <button title={u.is_active ? 'Deactivate' : 'Activate'} onClick={e => handleToggle(u.id, e)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: u.is_active ? 'var(--warn)' : 'var(--online)', padding: 4, borderRadius: 'var(--r-sm)' }}>
                  <Icon d={u.is_active ? OffIcon : OnIcon} color={u.is_active ? 'var(--warn)' : 'var(--online)'} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UsersTab;
